import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react'
import type { NtDay, NtTerm, NtAttendanceStatus } from '../../core/types'
import { useNorseTerm } from './NorseTermData'
import { DAY_LABELS } from './model'
import {
  STATUS_LABEL, notTaken, exceptions, courseDay, studentWeek, studentTotals, schoolRates, termDay, type MarkRow,
} from './attendanceLogic'
import { Empty } from '../../ui/States'

/**
 * Attendance for the office and admins. Four ways in, because four different
 * questions get asked during the week:
 *
 *   Day     — is it all in, and who is not here? (the one open most mornings)
 *   Student — a parent is on the phone: where was my kid on Tuesday afternoon?
 *   Grade   — who is missing the most, among the juniors?
 *   School  — how is the week going?
 *
 * Morning before afternoon everywhere. Desktop-primary — it is tables — with
 * the day view also usable on a phone.
 */

type View = 'day' | 'student' | 'grade' | 'school'
const BLOCK = { AM: 'Morning', PM: 'Afternoon' } as const

function StatusPill({ s }: { s: NtAttendanceStatus | null }) {
  if (s === null) return <span className="pill"><Clock size={11} aria-hidden="true" /> Not taken</span>
  const tone = s === 'present' ? 'pill--good' : s === 'absent' ? 'pill--critical' : 'pill--serious'
  return <span className={`pill ${tone}`}>{STATUS_LABEL[s]}</span>
}

export function AttendanceReport({ term }: { term: NtTerm }) {
  const { courses, allRosters: rosters, termAttendance: att } = useNorseTerm()
  const today = termDay(term)
  // Today during the week; otherwise the latest day anything was taken.
  const lastWithData = Math.max(-1, ...att.map((a) => a.day))
  const [day, setDay] = useState<NtDay>(today ?? (lastWithData >= 0 ? lastWithData : 0) as NtDay)
  const [view, setView] = useState<View>('day')
  const live = (courses ?? []).filter((c) => c.status === 'approved' && c.block)

  if (!live.length || !rosters.length) {
    return <Empty title="Nothing to report yet" body="Attendance reports start once placement has run and teachers have rosters." />
  }

  return (
    <div className="wide">
      <div className="chips" role="tablist" aria-label="Attendance view" style={{ margin: 'var(--s-2) 0 var(--s-3)' }}>
        {([['day', 'By day'], ['student', 'A student'], ['grade', 'A grade'], ['school', 'The school']] as Array<[View, string]>).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={view === k} className={`chip${view === k ? ' is-on' : ''}`} onClick={() => setView(k)}>{l}</button>
        ))}
      </div>
      {view === 'day' && <DayView day={day} setDay={setDay} today={today} />}
      {view === 'student' && <StudentView />}
      {view === 'grade' && <GradeView />}
      {view === 'school' && <SchoolView />}
    </div>
  )
}

function DayPicker({ day, setDay, today }: { day: NtDay; setDay: (d: NtDay) => void; today: NtDay | null }) {
  return (
    <div className="chips" role="tablist" aria-label="Day">
      {(DAY_LABELS as readonly string[]).map((l, i) => (
        <button key={l} role="tab" aria-selected={day === i} className={`chip${day === i ? ' is-on' : ''}`} onClick={() => setDay(i as NtDay)}>
          {l}{i === 3 ? ' (prep)' : ''}{today === i ? ' · today' : ''}
        </button>
      ))}
    </div>
  )
}

function DayView({ day, setDay, today }: { day: NtDay; setDay: (d: NtDay) => void; today: NtDay | null }) {
  const { courses, allRosters: rosters, termAttendance: att, artifactOf } = useNorseTerm()
  const missing = notTaken(courses ?? [], rosters, att, day, artifactOf)
  const out = exceptions(courses ?? [], rosters, att, day, artifactOf)
  const byCourse = courseDay(courses ?? [], rosters, att, day, artifactOf)
  const future = today !== null && day > today
  const absent = out.filter((r) => r.status === 'absent')

  return (
    <>
      <DayPicker day={day} setDay={setDay} today={today} />

      {/* The alert: who is not here. First, because it is the reason to look. */}
      <section className={`card ${absent.length ? 'card--alert' : ''}`} style={{ marginTop: 'var(--s-3)' }}>
        <h2 className="chk__title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {absent.length ? <AlertTriangle size={16} aria-hidden="true" /> : <CheckCircle2 size={16} aria-hidden="true" />}
          {absent.length ? `${absent.length} absent` : 'Nobody absent'}{out.length > absent.length && ` · ${out.length - absent.length} tardy`}
          <span className="work__sub" style={{ fontWeight: 400 }}> · {DAY_LABELS[day]}, from what has been taken</span>
        </h2>
        {out.length > 0 && <ExceptionsTable rows={out} />}
      </section>

      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <h2 className="chk__title">
          {missing.length ? `${missing.length} ${missing.length === 1 ? 'course has' : 'courses have'} not taken attendance` : 'Every course has taken attendance'}
        </h2>
        {future && missing.length > 0 && <p className="opt__hint" style={{ margin: '4px 0 0' }}>{DAY_LABELS[day]} has not happened yet.</p>}
        {day === 3 && <p className="opt__hint" style={{ margin: '4px 0 0' }}>Friday morning: each course has only the students making their artifact in it.</p>}
        {missing.length > 0 && (
          <ul className="dash__people">
            {missing.map((c) => (
              <li key={c.courseId}>
                <span className="pill">{BLOCK[c.block ?? 'AM']}</span>
                <span style={{ fontWeight: 600 }}>{c.title}</span>
                <span className="work__sub">{c.teacherName} · {c.room || 'no room'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <h2 className="work__heading">By course</h2>
      <table className="table">
        <thead><tr><th>Block</th><th>Course</th><th>Teacher</th><th className="num">Enrolled</th>
          <th className="num">Present</th><th className="num">Absent</th><th className="num">Tardy</th><th>Taken by</th></tr></thead>
        <tbody>
          {byCourse.map((c) => (
            <tr key={c.courseId}>
              <td>{BLOCK[c.block ?? 'AM']}</td><td style={{ fontWeight: 600 }}>{c.title}</td><td>{c.teacherName}</td>
              <td className="num">{c.enrolled}</td>
              {c.taken ? <>
                <td className="num">{c.counts.present}</td><td className="num">{c.counts.absent}</td>
                <td className="num">{c.counts['tardy-excused'] + c.counts['tardy-unexcused']}</td>
                <td>{c.takenBy ?? '—'}</td>
              </> : <td colSpan={4}><StatusPill s={null} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="work cards">
        {byCourse.map((c) => (
          <li key={c.courseId} className="work__row">
            <div className="work__main">
              <div style={{ fontWeight: 610 }}>{BLOCK[c.block ?? 'AM']} · {c.title}</div>
              <div className="work__sub">{c.teacherName}</div>
              <div className="work__status">
                {c.taken ? `${c.counts.present} present · ${c.counts.absent} absent · ${c.counts['tardy-excused'] + c.counts['tardy-unexcused']} tardy` : 'Not taken'}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

function ExceptionsTable({ rows }: { rows: MarkRow[] }) {
  return (
    <ul className="dash__people">
      {rows.map((r) => (
        <li key={`${r.courseId}_${r.studentId}`}>
          <StatusPill s={r.status} />
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          <span className="work__sub">
            Grade {r.grade} · {BLOCK[r.block ?? 'AM']} · {r.title} ({r.teacherName})
          </span>
          {r.note && <span className="att__office-note">“{r.note}”</span>}
        </li>
      ))}
    </ul>
  )
}

function StudentView() {
  const { courses, allRosters: rosters, termAttendance: att, artifactOf } = useNorseTerm()
  const [q, setQ] = useState('')
  const [pick, setPick] = useState<string | null>(null)
  const everyone = useMemo(() => {
    const m = new Map<string, { studentId: string; name: string; grade: number }>()
    rosters.forEach((r) => r.students.forEach((s) => m.set(s.studentId, s)))
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [rosters])
  const matches = q.trim().length < 2 ? [] : everyone.filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
  const who = everyone.find((s) => s.studentId === pick)
  const week = pick ? studentWeek(pick, courses ?? [], rosters, att, artifactOf) : []

  return (
    <>
      <input className="opt__input" placeholder="Find a student by name" value={q} aria-label="Find a student by name"
        onChange={(e) => { setQ(e.target.value); setPick(null) }} />
      {!pick && matches.length > 0 && (
        <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
          {matches.map((s) => (
            <li key={s.studentId} className="work__row">
              <button className="row-link" onClick={() => { setPick(s.studentId); setQ(s.name) }}>{s.name}</button>
              <span className="work__sub" style={{ marginLeft: 'auto' }}>Grade {s.grade}</span>
            </li>
          ))}
        </ul>
      )}
      {who && (
        <section className="card" style={{ marginTop: 'var(--s-3)' }}>
          <h2 className="chk__title">{who.name} <span className="work__sub" style={{ fontWeight: 400 }}>· Grade {who.grade}</span></h2>
          <table className="table att__grid">
            <thead><tr><th>Day</th><th>Morning</th><th>Afternoon</th></tr></thead>
            <tbody>
              {week.map((w) => (
                <tr key={w.day}>
                  <td style={{ fontWeight: 600 }}>{DAY_LABELS[w.day]}</td>
                  {(['AM', 'PM'] as const).map((b) => (
                    <td key={b}>
                      {w.day === 3 && b === 'PM' ? <span className="work__sub">—</span> : w[b].course ? <>
                        <div className="work__sub">{w[b].course!.title}</div>
                        <StatusPill s={w[b].status} />
                        {w[b].note && <div className="att__office-note">“{w[b].note}”</div>}
                      </> : <span className="work__sub">No course</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {week.map((w) => (
              <li key={w.day} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{DAY_LABELS[w.day]}</div>
                  {(['AM', 'PM'] as const).map((b) => (
                    <div key={b} className="work__sub" style={{ marginTop: 4 }}>
                      {BLOCK[b]}: {w[b].course ? <>{w[b].course!.title} <StatusPill s={w[b].status} /></> : 'no course'}
                      {w[b].note && <>: “{w[b].note}”</>}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

function Rates({ grade }: { grade?: number }) {
  const { courses, allRosters: rosters, termAttendance: att, artifactOf } = useNorseTerm()
  const rates = schoolRates(courses ?? [], rosters, att, grade, artifactOf)
  const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '—')
  return (
    <table className="table">
      <thead><tr><th>Day</th><th>Block</th><th className="num">Courses in</th><th className="num">Present</th>
        <th className="num">Absent</th><th className="num">Tardy</th><th className="num">Present rate</th></tr></thead>
      <tbody>
        {rates.map((r) => (
          <tr key={`${r.day}${r.block}`}>
            <td>{r.block === 'AM' ? DAY_LABELS[r.day] : ''}</td>
            <td>{r.day === 3 ? `Prep, ${BLOCK[r.block].toLowerCase()} courses` : BLOCK[r.block]}</td>
            <td className="num">{r.coursesTaken} of {r.coursesTotal}</td>
            <td className="num">{r.present}</td><td className="num">{r.absent}</td><td className="num">{r.tardy}</td>
            <td className="num">{pct(r.present, r.recorded)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function GradeView() {
  const { courses, allRosters: rosters, termAttendance: att, artifactOf } = useNorseTerm()
  const totals = studentTotals(courses ?? [], rosters, att, artifactOf)
  const grades = [...new Set(totals.map((t) => t.grade))].sort((a, b) => a - b)
  const [grade, setGrade] = useState<number>(grades[grades.length - 1] ?? 12)
  const mine = totals.filter((t) => t.grade === grade)
  return (
    <>
      <div className="chips" role="tablist" aria-label="Grade">
        {grades.map((g) => (
          <button key={g} role="tab" aria-selected={grade === g} className={`chip${grade === g ? ' is-on' : ''}`} onClick={() => setGrade(g)}>Grade {g}</button>
        ))}
      </div>
      <h2 className="work__heading">Grade {grade} · {mine.length} students · most absences first</h2>
      <table className="table">
        <thead><tr><th>Student</th><th className="num">Absent</th><th className="num">Tardy (excused)</th>
          <th className="num">Tardy (unexcused)</th><th className="num">Sessions recorded</th></tr></thead>
        <tbody>
          {mine.map((t) => (
            <tr key={t.studentId} className={t.absent ? 'is-flag' : ''}>
              <td style={{ fontWeight: 600 }}>{t.name}</td>
              <td className="num">{t.absent}</td><td className="num">{t.tardyExcused}</td>
              <td className="num">{t.tardyUnexcused}</td><td className="num">{t.recorded}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="work cards">
        {mine.map((t) => (
          <li key={t.studentId} className="work__row">
            <div className="work__main">
              <div style={{ fontWeight: 610 }}>{t.name}</div>
              <div className="work__sub">{t.absent} absent · {t.tardyExcused + t.tardyUnexcused} tardy · {t.recorded} recorded</div>
            </div>
          </li>
        ))}
      </ul>
      <h2 className="work__heading">Grade {grade} by day</h2>
      <Rates grade={grade} />
    </>
  )
}

function SchoolView() {
  const { courses, allRosters: rosters, termAttendance: att, artifactOf } = useNorseTerm()
  const totals = studentTotals(courses ?? [], rosters, att, artifactOf)
  const grades = [...new Set(totals.map((t) => t.grade))].sort((a, b) => a - b)
  return (
    <>
      <h2 className="work__heading">The school by day</h2>
      <Rates />
      <h2 className="work__heading">By grade, across the week</h2>
      <table className="table">
        <thead><tr><th>Grade</th><th className="num">Students</th><th className="num">With an absence</th>
          <th className="num">Absences</th><th className="num">Tardies</th></tr></thead>
        <tbody>
          {grades.map((g) => {
            const rows = totals.filter((t) => t.grade === g)
            return (
              <tr key={g}>
                <td style={{ fontWeight: 600 }}>Grade {g}</td><td className="num">{rows.length}</td>
                <td className="num">{rows.filter((r) => r.absent).length}</td>
                <td className="num">{rows.reduce((n, r) => n + r.absent, 0)}</td>
                <td className="num">{rows.reduce((n, r) => n + r.tardyExcused + r.tardyUnexcused, 0)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}
